import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

/**
 * Keeps in-memory user in sync with `localStorage.user`.
 * The native `storage` event does not fire in the same tab that called `setItem`,
 * so components must call {@link refreshFromStorage} after updating `user` locally.
 */
@Injectable({ providedIn: 'root' })
export class UserStateService {
  private readonly userSubject = new BehaviorSubject<unknown | null>(this.readUserFromStorage());

  /** Latest user object from storage; updates when {@link refreshFromStorage} runs. */
  readonly user$: Observable<unknown | null> = this.userSubject.asObservable();

  private readUserFromStorage(): unknown | null {
    try {
      const raw = localStorage.getItem('user');
      if (!raw) {
        return null;
      }
      return JSON.parse(raw) as unknown;
    } catch {
      return null;
    }
  }

  /** Snapshot for initial render before subscriptions run. */
  getCurrentUser(): unknown | null {
    return this.userSubject.getValue();
  }

  /** Call after any in-tab `localStorage.setItem('user', ...)` or `removeItem('user')`. */
  refreshFromStorage(): void {
    this.userSubject.next(this.readUserFromStorage());
  }
}
